"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Shield,
  Target,
  User,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { POZICIA_LABELS, STRANA_HOKEJKY_LABELS } from "@/lib/utils";
import type { PoziciaHraca, strana_hokejky } from "@/types/database";

interface NavrhHracaFormProps {
  user: { id: string } | null;
}

export function NavrhHracaForm({ user }: NavrhHracaFormProps) {
  const PAGE_TITLE = "Údaje o hráčovi";
  const PAGE_DESC = "Vyplňte všetky dostupné informácie o navrhovanom hráčovi";
  const NAME_LABEL = "Celé meno";
  const POSITION_LABEL = "Pozícia";
  const SIDE_LABEL = "Strana hokejky";
  const CLUB_LABEL = "Klub";
  const COMPETITION_LABEL = "Súťaž/Liga";
  const BIRTH_LABEL = "Dátum narodenia";
  const INCOME_LABEL = "Ročný príjem ($)";
  const VALUE_LABEL = "Trhová hodnota ($)";
  const NOTE_LABEL = "Poznámka";
  const AVATAR_LABEL = "URL adresy fotky";
  const INFO_BOX_TITLE = "Dôležité informácie:";
  const SENDING_TEXT = "Odosielam návrh...";
  const SEND_TEXT = "Odoslať návrh hráča";

  const router = useRouter();

  const supabase = createClient();
  const [loading, setLoading] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Form state
  const [cele_meno, setcele_meno] = useState("");
  const [pozicia, setPozicia] = useState<PoziciaHraca | "">("");
  const [strana_hokejky, setStranaHokejky] = useState<strana_hokejky | "">("");
  const [sutaz, setSutaz] = useState("");
  const [klub, setKlub] = useState("");
  const [datum_narodenia, setDatumNarodenia] = useState("");
  const [prijem, setPrijem] = useState<number | "">(0);
  const [hodnota_hraca, setHodnotaHraca] = useState<number | "">(0);
  const [poznamka, setPoznamka] = useState("");
  const [avatar_url, setAvatarUrl] = useState("");
  const [duplicateStatus, setDuplicateStatus] = useState<
    "none" | "checking" | "exists" | "available"
  >("none");

  // Redirect if not authenticated (client-side backup)
  useEffect(() => {
    if (!user) {
      router.push("/prihlasenie?redirect=/navrh-hraca");
    }
  }, [user, router]);

  const checkDuplicatePlayer = useCallback(
    async (meno: string) => {
      if (!meno.trim() || meno.length < 3) {
        setDuplicateStatus("none");
        return;
      }

      setCheckingDuplicate(true);
      setDuplicateStatus("checking");

      try {
        const { data, error } = await supabase.rpc("kontrola_duplicity_hraca", {
          p_cele_meno: meno.trim(),
        });

        if (error) throw error;

        setDuplicateStatus(data ? "exists" : "available");
      } catch {
        setDuplicateStatus("none");
      } finally {
        setCheckingDuplicate(false);
      }
    },
    [supabase],
  );

  // Debounced duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cele_meno) {
        checkDuplicatePlayer(cele_meno);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [cele_meno, checkDuplicatePlayer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !pozicia) {
      toast.error("Vyplňte všetky povinné polia");
      return;
    }

    if (duplicateStatus === "exists") {
      toast.error("Hráč s týmto menom už existuje");
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.from("navrhhraca").insert({
        cele_meno: cele_meno.trim(),
        pozicia: pozicia as PoziciaHraca,
        strana_hokejky: (strana_hokejky as strana_hokejky) || null,
        sutaz: sutaz.trim() || null,
        klub: klub.trim() || null,
        datum_narodenia: datum_narodenia || null,
        prijem: prijem || 0,
        hodnota_hraca: hodnota_hraca || 0,
        poznamka: poznamka.trim() || null,
        avatar_url: avatar_url.trim() || null,
        navrhnute_profil_id: user.id,
      });

      if (error) throw error;

      toast.success("Návrh hráča bol úspešne odoslaný na schválenie!");

      // Reset form
      setcele_meno("");
      setPozicia("");
      setStranaHokejky("");
      setSutaz("");
      setKlub("");
      setDatumNarodenia("");
      setPrijem(0);
      setHodnotaHraca(0);
      setPoznamka("");
      setAvatarUrl("");
      setDuplicateStatus("none");
    } catch {
      toast.error("Chyba pri odosielaní návrhu hráča");
    } finally {
      setLoading(false);
    }
  };

  const getPoziciaIcon = (poz: PoziciaHraca) => {
    switch (poz) {
      case "brankar":
        return <Shield className="h-4 w-4" />;
      case "obranca":
        return <User className="h-4 w-4" />;
      case "utocnik":
        return <Target className="h-4 w-4" />;
    }
  };

  const getDuplicateStatusBadge = () => {
    switch (duplicateStatus) {
      case "checking":
        return (
          <Badge
            variant="outline"
            className="text-muted-foreground bg-muted/50 border-muted-foreground/20"
          >
            <div className="animate-spin h-3 w-3 border border-muted-foreground border-t-transparent rounded-full mr-1" />
            Kontrolujem...
          </Badge>
        );
      case "exists":
        return (
          <Badge
            variant="destructive"
            className="bg-destructive/15 text-destructive border-destructive/20"
          >
            <AlertCircle className="h-3 w-3 mr-1" />
            Hráč už existuje
          </Badge>
        );
      case "available":
        return (
          <Badge
            variant="outline"
            className="text-muted-foreground bg-muted/50 border-muted-foreground/20"
          >
            <CheckCircle className="h-3 w-3 mr-1" />
            Dostupné
          </Badge>
        );
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Card className="border-muted/20 shadow-sm bg-background/50 backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Plus className="h-5 w-5 text-primary" />
          {PAGE_TITLE}
        </CardTitle>
        <CardDescription className="text-base">{PAGE_DESC}</CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          {/* Celé meno */}
          <div className="space-y-2">
            <Label
              htmlFor="cele_meno"
              className="text-sm font-medium text-foreground"
            >
              {NAME_LABEL} <span className="text-destructive">*</span>
            </Label>
            <div className="space-y-2">
              <Input
                id="cele_meno"
                type="text"
                placeholder="Napr. Juraj Slafkovský"
                value={cele_meno}
                onChange={(e) => setcele_meno(e.target.value)}
                required
                className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
              />
              {cele_meno && getDuplicateStatusBadge()}
            </div>
          </div>

          {/* Pozícia */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <div className="space-y-2">
              <Label
                htmlFor="pozicia"
                className="text-sm font-medium text-foreground"
              >
                {POSITION_LABEL} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={pozicia}
                onValueChange={(value) => setPozicia(value as PoziciaHraca)}
              >
                <SelectTrigger className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0">
                  <SelectValue placeholder="Vyberte pozíciu" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(POZICIA_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      <div className="flex items-center gap-2">
                        {getPoziciaIcon(value as PoziciaHraca)}
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="strana_hokejky"
                className="text-sm font-medium text-foreground"
              >
                {SIDE_LABEL}
              </Label>
              <Select
                value={strana_hokejky}
                onValueChange={(value) =>
                  setStranaHokejky(value as strana_hokejky)
                }
              >
                <SelectTrigger className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0">
                  <SelectValue placeholder="Ľavák / Pravák" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STRANA_HOKEJKY_LABELS).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Klub a Súťaž */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <div className="space-y-2">
              <Label
                htmlFor="klub"
                className="text-sm font-medium text-foreground"
              >
                {CLUB_LABEL}
              </Label>
              <Input
                id="klub"
                type="text"
                placeholder="Napr. Montreal Canadiens"
                value={klub}
                onChange={(e) => setKlub(e.target.value)}
                className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="sutaz"
                className="text-sm font-medium text-foreground"
              >
                {COMPETITION_LABEL}
              </Label>
              <Input
                id="sutaz"
                type="text"
                placeholder="Napr. NHL, KHL"
                value={sutaz}
                onChange={(e) => setSutaz(e.target.value)}
                className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
              />
            </div>
          </div>

          {/* Dátum narodenia */}
          <div className="space-y-2">
            <Label
              htmlFor="datum_narodenia"
              className="text-sm font-medium text-foreground"
            >
              {BIRTH_LABEL}
            </Label>
            <Input
              id="datum_narodenia"
              type="date"
              value={datum_narodenia}
              onChange={(e) => setDatumNarodenia(e.target.value)}
              className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
            />
          </div>

          {/* Finančné údaje */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <div className="space-y-2">
              <Label
                htmlFor="prijem"
                className="text-sm font-medium text-foreground"
              >
                {INCOME_LABEL}
              </Label>
              <Input
                id="prijem"
                type="number"
                placeholder="0"
                value={prijem}
                onChange={(e) =>
                  setPrijem(e.target.value === "" ? "" : Number(e.target.value))
                }
                className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="hodnota_hraca"
                className="text-sm font-medium text-foreground"
              >
                {VALUE_LABEL}
              </Label>
              <Input
                id="hodnota_hraca"
                type="number"
                placeholder="0"
                value={hodnota_hraca}
                onChange={(e) =>
                  setHodnotaHraca(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
              />
            </div>
          </div>

          {/* Poznámka */}
          <div className="space-y-2">
            <Label
              htmlFor="poznamka"
              className="text-sm font-medium text-foreground"
            >
              {NOTE_LABEL}
            </Label>
            <Textarea
              id="poznamka"
              placeholder="Doplňujúce informácie o hráčovi (voliteľné)"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              rows={3}
              className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0 resize-none"
            />
          </div>

          {/* Avatar URL */}
          <div className="space-y-2">
            <Label
              htmlFor="avatar_url"
              className="text-sm font-medium text-foreground"
            >
              {AVATAR_LABEL}
            </Label>
            <Input
              id="avatar_url"
              type="url"
              placeholder="https://priklad.cz/fotka.jpg"
              value={avatar_url}
              onChange={(e) => setAvatarUrl(e.target.value)}
              className="text-base border-muted/20 bg-transparent focus:border-primary focus:ring-0"
            />
            <p className="text-xs text-muted-foreground">
              Odkaz na fotku hráča (voliteľné)
            </p>
          </div>

          {/* Info box */}
          <div className="bg-muted/30 border border-muted/20 rounded-lg p-4">
            <h4 className="font-bold text-foreground mb-2">{INFO_BOX_TITLE}</h4>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>• Všetky návrhy budú posúdené administrátormi</li>
              <li>• O schválení/zamietnutí budete informovaní</li>
              <li>• Kontrolujte, či hráč už nie je v databáze</li>
              <li>• Uvádzajte správne a aktuálne informácie</li>
            </ul>
          </div>
        </CardContent>

        <CardFooter>
          <Button
            type="submit"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-md hover:shadow-lg transition-all"
            disabled={
              loading ||
              duplicateStatus === "exists" ||
              checkingDuplicate ||
              !cele_meno ||
              !pozicia
            }
            size="lg"
          >
            {loading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full mr-2" />
                {SENDING_TEXT}
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {SEND_TEXT}
              </>
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
